import React, { useState } from 'react';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import { Designer } from '@graphql/Designer/queries/availableDesigners.graphql';
import breakpoints from '@constants/breakpoints';
import themeStyles from '@components/theme/default/styles.module.scss';
import CarouselNavigator from '@components/shared/carousels/CarouselNavigator/CarouselNavigator';
import trackHomeGetStarted from '@components/pages/Home/lib/trackHomeGetStarted';
import DesignerCard from './DesignerCard';
import styles from './FeaturedDesigners.module.scss';

SwiperCore.use([Navigation]);

const MIN_DESIGNERS = 4;
const FADE_LEFT = '32px';
const FADE_RIGHT = '32px';

interface IFeaturedDesignersProps {
  designers?: Designer[];
  eyebrow?: string;
  title?: string;
  body?: string;
  ctaHref?: string;
  ctaLabel?: string;
  getStartedCtaSection?: string;
  // Show the In-Person/Online tag on each card. On only for the city location
  // pages, where service type drives the choice; off elsewhere (e.g. homepage).
  showServiceTag?: boolean;
}

export default function FeaturedDesigners({
  designers = [],
  eyebrow = 'Havenly Designers',
  title = 'Meet our professional designers',
  body = "Your very own designer, dedicated to you. They'll learn your taste, understand your life, and create something that feels genuinely yours.",
  ctaHref = '/interior-design-style-quiz/style-inspiration',
  ctaLabel = 'Get Started',
  getStartedCtaSection,
  showServiceTag = false,
}: IFeaturedDesignersProps) {
  const [edges, setEdges] = useState({ canLeft: false, canRight: true });

  if (designers.length < MIN_DESIGNERS) return null;

  const handleEdgesUpdate = (swiper: SwiperCore) => {
    const canLeft = !swiper.isBeginning;
    const canRight = !swiper.isEnd;
    setEdges((prev) => (
      prev.canLeft === canLeft && prev.canRight === canRight
        ? prev
        : { canLeft, canRight }
    ));
  };

  const maskGradient = edges.canLeft || edges.canRight
    ? `linear-gradient(to right, ${
      edges.canLeft ? `transparent 0, black ${FADE_LEFT}` : 'black 0'
    }, ${
      edges.canRight ? `black calc(100% - ${FADE_RIGHT}), transparent 100%` : 'black 100%'
    })`
    : undefined;

  const swiperStyle: React.CSSProperties | undefined = maskGradient
    ? { maskImage: maskGradient, WebkitMaskImage: maskGradient }
    : undefined;

  return (
    <section className={styles.Section} data-test="featured-designers">
      <div className={styles.Container}>
        <p className={styles.Eyebrow}>{eyebrow}</p>
        <h2 className={styles.Title}>{title}</h2>
        <p className={styles.Body}>{body}</p>

        <div className={styles.SwiperWrapper}>
          <Swiper
            slidesPerView="auto"
            spaceBetween={16}
            grabCursor
            navigation={{
              nextEl: '.featured-designers-next',
              prevEl: '.featured-designers-prev',
            }}
            breakpoints={{
              [breakpoints['large-tablet']]: {
                spaceBetween: 20,
              },
            }}
            className={styles.Swiper}
            style={swiperStyle}
            onSwiper={handleEdgesUpdate}
            onSlideChange={handleEdgesUpdate}
            onResize={handleEdgesUpdate}
            onReachBeginning={handleEdgesUpdate}
            onReachEnd={handleEdgesUpdate}
            onFromEdge={handleEdgesUpdate}
          >
            {designers.map((designer) => (
              <SwiperSlide
                key={designer.id}
                className={classNames(styles.Slide, {
                  [styles.SlideWithServiceTag]: showServiceTag,
                })}
              >
                <DesignerCard designer={designer} showServiceTag={showServiceTag} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <CarouselNavigator
          className={styles.Pagination}
          navNext="featured-designers-next"
          navPrev="featured-designers-prev"
        />

        <a
          href={ctaHref}
          onClick={(e) => {
            if (getStartedCtaSection) {
              trackHomeGetStarted(e.currentTarget, getStartedCtaSection);
            }
          }}
          className={classNames(
            themeStyles.Button,
            themeStyles.Primary,
            themeStyles.Medium,
            styles.CTAButton
          )}
        >
          {ctaLabel}
        </a>
      </div>
    </section>
  );
}
